import { useEffect, useState } from "react";

import get from "lodash/get";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Timeline from "@mui/lab/Timeline";
import { timelineItemClasses } from "@mui/lab/TimelineItem";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";

import {
  SpacerTimelineItem,
  TimelineConnector,
  TimelineContent,
  TimelineEndDot,
  TimelineOppositeContent,
  TimelineSeparator,
} from "./StyledTimelineComponents";

import type { GroupedMeasurement, GroupedPanel, WorkflowReducer } from "../../workflowReducer";
import { MeasurementGroupTimelineItem } from "./MeasurementGroupTimelineItem";

import { AddButton } from "../../../shared/FormLayout/IconButtons/AddButton";
import { ValidationMessage } from "../../../shared/ValidationMessage";
import { PanelMeasurementsAdd } from "../PanelMeasurementsAdd";

interface Props {
  workflowReducer: WorkflowReducer;
  editMode?: boolean;
}

const useStyles = makeStyles((theme) => ({
  measurementsContainer: {
    display: "flex",
    flexDirection: "column",
    marginTop: theme.spacing(2),
  },
  actions: {
    display: "flex",
    justifyContent: "center",
  },
  emptyPlaceholder: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

export function MeasurementsTimeline(props: Props) {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();

  const [panelDialogOpen, setPanelDialogOpen] = useState(false);
  const [panelEditingMeasurementGroupId, setPanelEditingMeasurementGroupId] = useState<string | undefined>();

  const measurementGroupCount = props.workflowReducer.state.groupedMeasurements.length;
  const switchgearId =
    props.workflowReducer.state.workflow.workflowDetail.switchgear_id ||
    getGroupedMeasurementFromState(panelEditingMeasurementGroupId)?.parentAssetId;
  const panelsToExclude = getGroupedMeasurementFromState(panelEditingMeasurementGroupId)?.panels;

  useEffect(() => {
    register(`measurementsTab.measurementGroupCount`, {
      validate: {
        positive: (value) => value > 0,
      },
    });
  }, [register]);

  useEffect(() => {
    setValue(`measurementsTab.measurementGroupCount`, measurementGroupCount, {
      shouldValidate: measurementGroupCount > 0,
    });
  }, [setValue, measurementGroupCount]);

  function handlePanelAddButtonClick(groupId: string) {
    setPanelEditingMeasurementGroupId(groupId);

    openPanelDialog();
  }

  function handleAddPanelsDialogClick(panels: GroupedPanel[]) {
    closePanelDialog();
    panelEditingMeasurementGroupId && props.workflowReducer.addPanelsToGroup(panelEditingMeasurementGroupId, panels);
    setValue(`measurementsTab.${panelEditingMeasurementGroupId}.panelCount`, panels.length, { shouldValidate: true });
  }

  function handleGroupAddButtonClick() {
    props.workflowReducer.addNewMeasurementGroup();
    setValue(`measurementsTab.measurementGroupCount`, measurementGroupCount, {
      shouldValidate: true,
    });
  }

  function openPanelDialog() {
    setPanelDialogOpen(true);
  }

  function closePanelDialog() {
    setPanelDialogOpen(false);
    setPanelEditingMeasurementGroupId(undefined);
  }

  function getGroupedMeasurementFromState(groupId: string | undefined) {
    return groupId
      ? props.workflowReducer.state.groupedMeasurements.find(
          (groupedMeasurement: GroupedMeasurement) => groupedMeasurement.groupId === groupId,
        )
      : undefined;
  }

  return (
    <Paper variant="outlined" className={classes.measurementsContainer}>
      {props.workflowReducer.state.groupedMeasurements?.length > 0 ? (
        <div>
          <div>
            <div>
              <Timeline
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0,
                  },
                }}
              >
                {props.workflowReducer.state.groupedMeasurements?.map((measurement, index) => {
                  const isFirstItemInTimeline = index === 0;
                  const isLastItemInTimeline = index === measurementGroupCount - 1;

                  return (
                    <div key={measurement.groupId}>
                      <span>
                        <MeasurementGroupTimelineItem
                          workflowReducer={props.workflowReducer}
                          measurement={measurement}
                          onPanelAddButtonClick={handlePanelAddButtonClick}
                          editMode={props.editMode}
                          isFirstItemInTimeline={isFirstItemInTimeline}
                        />
                        <SpacerTimelineItem>
                          <TimelineOppositeContent />
                          <TimelineSeparator>
                            <TimelineConnector />
                            {isLastItemInTimeline && <TimelineEndDot />}
                          </TimelineSeparator>
                          <TimelineContent />
                        </SpacerTimelineItem>
                      </span>
                    </div>
                  );
                })}
              </Timeline>
            </div>
          </div>
        </div>
      ) : (
        <span className={classes.emptyPlaceholder}>
          <Typography color="textSecondary" variant="subtitle2">
            {t("workflows.measurementsEmpty")}{" "}
          </Typography>
          {props.editMode && get(errors, `measurementsTab.measurementGroupCount`) && (
            <ValidationMessage invalidMessage={t("workflows.measurementsRequired")} validationType="error" showIcon />
          )}
        </span>
      )}

      {props.editMode && (
        <div className={classes.actions}>
          <AddButton onClick={handleGroupAddButtonClick} />
        </div>
      )}

      {props.editMode && (
        <PanelMeasurementsAdd
          open={panelDialogOpen}
          onCancelClick={closePanelDialog}
          onChangePanelsClick={handleAddPanelsDialogClick}
          switchgearId={switchgearId}
          excludePanels={panelsToExclude}
        />
      )}
    </Paper>
  );
}
